'use client';

import { emitReviewsEvent, type ReviewsProvider } from '@/src/analytics/hotpads/events/reviews';
import dynamic from 'next/dynamic';
import { useApartmentReviewsContext } from './ApartmentReviewsProvider';
import { ReviewsShell, type ReviewSourceConfig } from './ReviewsShell';
import { ReviewsEntryPoint } from './ReviewsEntryPoint';
import { ReviewsEntrySkeleton, ReviewsPanelSkeleton } from './ReviewsSkeleton';
import RedditReviewsPanel from './RedditReviewsPanel';

const GoogleReviewsPanel = dynamic(() => import('./GoogleReviewsPanel'), {
  loading: () => <ReviewsPanelSkeleton source="google" />,
});
const ReputationApiPanel = dynamic(() => import('./ReputationListingPanel'), {
  loading: () => <ReviewsPanelSkeleton source="web" />,
});
export function ApartmentReviewsModule() {
  const { listing, propertyListing, lotId, reviews, isLoading, googlePlaceId, redditTopics } =
    useApartmentReviewsContext();
  if (!isLoading && !reviews && !googlePlaceId && !redditTopics.length) return null;
  const sources: Array<ReviewSourceConfig> = [];
  const reputation = reviews?.reputation;
  if (reputation)
    sources.push({
      id: 'web',
      label: 'Web reviews',
      panel: {
        status: 'ready',
        render: () => (
          <ReputationApiPanel
            listing={propertyListing}
            data={reputation}
            onInteraction={(interaction) => emitReviewsEvent(listing, interaction)}
          />
        ),
      },
    });
  if (googlePlaceId)
    sources.push({
      id: 'google',
      label: 'Google',
      instanceKey: googlePlaceId,
      eager: true,
      panel: {
        status: 'ready',
        render: () => <GoogleReviewsPanel placeId={googlePlaceId} />,
      },
    });
  if (redditTopics.length)
    sources.push({
      id: 'reddit',
      label: 'Reddit',
      panel: {
        status: 'ready',
        render: () => (
          <RedditReviewsPanel
            topics={redditTopics}
            onTopicClick={(topicId) =>
              emitReviewsEvent(listing, { event: 'reviews_reddit_topic_click', provider: 'reddit', topicId })
            }
          />
        ),
      },
    });
  return (
    <ReviewsShell
      key={`${listing?.aliasEncoded}:${lotId}`}
      id="reviews"
      loading={isLoading}
      sources={sources}
      onImpression={(provider, availableProviders) =>
        emitReviewsEvent(listing, { event: 'reviews_module_impression', provider, availableProviders })
      }
      onProviderChange={(provider, previousProvider) =>
        emitReviewsEvent(listing, { event: 'reviews_provider_change', provider, previousProvider })
      }
    />
  );
}

export function ApartmentReviewsEntry() {
  const { listing, reviews, isLoading, googlePlaceId, redditTopics } = useApartmentReviewsContext();
  if (isLoading) return <ReviewsEntrySkeleton />;
  if (!reviews && !googlePlaceId && !redditTopics.length) return null;
  const { averageRating, totalReviews } = reviews?.reputation ?? {};
  return (
    <ReviewsEntryPoint
      targetId="reviews"
      onEntryClick={() => {
        const availableProviders: Array<ReviewsProvider> = [];
        if (reviews?.reputation) availableProviders.push('web');
        if (googlePlaceId) availableProviders.push('google');
        if (redditTopics.length) availableProviders.push('reddit');
        emitReviewsEvent(listing, { event: 'reviews_entry_click', availableProviders });
      }}
      hasReputation={!!reviews?.reputation}
      aggregateRating={
        averageRating !== undefined && totalReviews !== undefined
          ? { rating: averageRating, reviewCount: totalReviews }
          : null
      }
      hasGooglePlace={!!googlePlaceId}
      hasRedditLinks={redditTopics.length > 0}
    />
  );
}
